import React, { useState, useEffect } from 'react';

// SVG chart primitives for the manager dashboard. Ported from the Nodal
// design-system template (templates/nodal-dashboard/charts-dc.jsx).

function useReady(delay = 60) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return ready;
}

export function Donut({ segments = [], label = '', sub = '', size = 184, thickness = 24 }) {
  const ready = useReady();
  const total = segments.reduce((acc, s) => acc + (Number(s.value) || 0), 0) || 1;
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const C = 2 * Math.PI * r;
  const gap = 2.5;
  let acc = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#eef2f7" strokeWidth={thickness} />
      <g transform={`rotate(-90 ${cx} ${cx})`}>
        {segments.map((s, i) => {
          const frac = (Number(s.value) || 0) / total;
          const len = Math.max(frac * C - gap, 0);
          const offset = -acc;
          acc += frac * C;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeLinecap="round"
              strokeDasharray={`${ready ? len : 0} ${C}`}
              strokeDashoffset={offset}
              style={{ transition: `stroke-dasharray .9s cubic-bezier(.22,1,.36,1) ${i * 0.08}s` }}
            />
          );
        })}
      </g>
      <text
        x={cx}
        y={cx - 4}
        textAnchor="middle"
        style={{ font: '700 30px var(--font-sans, inherit)', fill: 'var(--text-heading)', letterSpacing: '-0.02em' }}
      >
        {label}
      </text>
      <text
        x={cx}
        y={cx + 16}
        textAnchor="middle"
        style={{ font: '600 11px var(--font-sans, inherit)', fill: 'var(--text-subtle)', letterSpacing: '0.04em', textTransform: 'uppercase' }}
      >
        {sub}
      </text>
    </svg>
  );
}

export function Spark({ data = [], color = '#185FA5', tint = false, w = 76, h = 26 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const x = (i) => (i * w) / (data.length - 1);
  const y = (v) => h - 3 - ((v - min) / (max - min || 1)) * (h - 6);
  const pts = data.map((v, i) => [x(i), y(v)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      {tint && <path d={`${line} L${w},${h} L0,${h} Z`} fill={color} opacity={0.13} />}
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2.6} fill={color} />
    </svg>
  );
}
